import { DateTime } from '@ksyos/date-time';
import type { Uuid } from '@common/utils/uuid';
import { type FundingServicePart, getFundingServiceParts } from '@frontend/services/api/fundingServicePartsApiService';
import type { IFundingSetting } from '../../../../common/interfaces/fundingSetting';
import { UrlBuilder } from '../../../../common/utils/urlBuilder';
import { fetchJson } from '../../../store/fetch';
import { translateKey } from '../../../translate';
import {
    AdminTable,
    DeleteButton,
    EditButton,
    FormHeader,
    LinkButton,
    Loading,
    NullableBoolean,
    TableBody,
    TableData,
    TableHead,
    TableHeader,
    TableRow,
} from '../../elements/common';
import { Component } from '../../elements/Component';
import { FormatCurrency } from '../../elements/FormatCurrency';

interface IFundingSettingsListProps {
    groupId?: string;
    organizationId?: string;
    serviceGroupSettingId?: string;
    serviceId?: string;
    serviceOrganizationSettingId?: string;
}

interface IFundindSettingWithAuth extends IFundingSetting {
    authorization: {
        canDelete: boolean;
        canUpdate: boolean;
    };
}

interface IFundingSettingsListState {
    authorization: {
        canAdd: boolean;
    };
    fundingServiceParts: FundingServicePart[];
    fundingSettings: IFundindSettingWithAuth[];
}

export class FundingSettingsList extends Component<IFundingSettingsListProps, IFundingSettingsListState> {
    private readonly abortController = new AbortController();

    constructor(props: IFundingSettingsListProps, context?: {}) {
        super(props, context);

        this.state = {
            fundingSettings: [],
            authorization: {
                canAdd: false,
            },
            fundingServiceParts: [],
        };
    }

    public componentDidMount() {
        this.loadFundingSettings();
    }

    public componentWillUnmount() {
        this.abortController.abort();
    }

    public render() {
        let actionButtons: JSX.Element[] | undefined;
        if (this.state.authorization.canAdd) {
            actionButtons = [
                <LinkButton
                    icon="add"
                    to={this.getUrlWithParams('/fundingSettings/add')}
                >
                    {translateKey('fundingSettings.add')}
                </LinkButton>,
            ];
        }

        return (
            <Loading
                item={this.state.fundingSettings}
                loaded={(fundingSettings) => (
                    <>
                        <FormHeader actionButtons={actionButtons}>{translateKey('common.invoice')}</FormHeader>
                        <AdminTable>
                            <TableHead>
                                <TableRow>
                                    <TableHeader>{translateKey('common.startDate')}</TableHeader>
                                    <TableHeader>{translateKey('common.endDate')}</TableHeader>
                                    <TableHeader>{translateKey('serviceProvider.sendInvoice')}</TableHeader>
                                    <TableHeader>{translateKey('finance.fundingServicePart.shortName')}</TableHeader>
                                    <TableHeader>{translateKey('serviceProvider.compensation')}</TableHeader>
                                    <TableHeader>{translateKey('groupSettings.isAlternativeFinancing')}</TableHeader>
                                    <TableHeader>{translateKey('groupSettings.alreadyFinanced')}</TableHeader>
                                    <TableHeader></TableHeader>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {fundingSettings.map((fundingSetting, key) => (
                                    <TableRow key={key}>
                                        <TableData>
                                            {DateTime.from(fundingSetting.startDate).format('DD-MM-YYYY')}
                                        </TableData>
                                        <TableData>
                                            {fundingSetting.endDate
                                                ? DateTime.from(fundingSetting.endDate).format('DD-MM-YYYY')
                                                : ''}
                                        </TableData>
                                        <TableData>
                                            <NullableBoolean value={fundingSetting.sendInvoice} />
                                        </TableData>
                                        <TableData>
                                            {this.mapFundingSettingToFundingServicePart(fundingSetting)}
                                        </TableData>
                                        <TableData>
                                            <FormatCurrency value={fundingSetting.compensationCharge || undefined} />
                                        </TableData>
                                        <TableData>
                                            <NullableBoolean value={fundingSetting.isAlternativeFinancing} />
                                        </TableData>
                                        <TableData>
                                            <NullableBoolean value={fundingSetting.financedByPreviousService} />
                                        </TableData>
                                        {fundingSetting.authorization.canUpdate && (
                                            <TableData>
                                                <EditButton
                                                    to={this.getUrlWithParams(`/fundingSettings/${fundingSetting.id}`)}
                                                />
                                                {fundingSetting.authorization.canDelete && (
                                                    <DeleteButton onClick={() => this.remove(fundingSetting.id!)} />
                                                )}
                                            </TableData>
                                        )}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </AdminTable>
                    </>
                )}
            />
        );
    }

    private async loadFundingSettings() {
        await this.loadFundingServiceParts();
        const urlBuilder = new UrlBuilder('/api/fundingSettings');
        urlBuilder.add('includeListAuthorizations', 'canAdd');
        urlBuilder.add('includeItemAuthorizations', 'canUpdate,canDelete');

        if (this.props.serviceGroupSettingId) {
            urlBuilder.add('serviceGroupSettingId', this.props.serviceGroupSettingId);
        } else if (this.props.serviceOrganizationSettingId) {
            urlBuilder.add('serviceOrganizationSettingId', this.props.serviceOrganizationSettingId);
        }

        const result = await fetchJson(urlBuilder.build(), 'GET');
        const items = result.json.items.sort((a: IFundingSetting, b: IFundingSetting) =>
            a.startDate < b.startDate ? 1 : -1
        );
        await this.setState({ fundingSettings: items, authorization: result.json.authorization });
    }

    private async remove(id: string) {
        const url = '/api/fundingSettings/' + id;
        await fetchJson(url, 'DELETE');
        await this.loadFundingSettings();
    }

    private getUrlWithParams(baseUrl: string) {
        const urlBuilder = new UrlBuilder(baseUrl);

        if (this.props.serviceGroupSettingId) {
            urlBuilder.add('serviceGroupSettingId', this.props.serviceGroupSettingId);
        } else if (this.props.serviceOrganizationSettingId) {
            urlBuilder.add('serviceOrganizationSettingId', this.props.serviceOrganizationSettingId);
        }

        if (this.props.serviceId) {
            urlBuilder.add('serviceId', this.props.serviceId);
        }
        if (this.props.groupId) {
            urlBuilder.add('groupId', this.props.groupId);
        }

        if (this.props.organizationId) {
            urlBuilder.add('organizationId', this.props.organizationId);
        }

        return urlBuilder.build();
    }

    private async loadFundingServiceParts() {
        const serviceId = this.props.serviceId;
        if (!serviceId) {
            throw new Error('Service id is required');
        }
        const parts = await getFundingServiceParts(serviceId as Uuid, this.abortController.signal);
        await this.setState({ fundingServiceParts: parts });
    }

    private mapFundingSettingToFundingServicePart(fundingSetting: IFundingSetting): string {
        if (!fundingSetting.sendInvoice) {
            return '';
        }
        if (fundingSetting.fundingServicePartId) {
            const part = this.state.fundingServiceParts.find((p) => p.id === fundingSetting.fundingServicePartId);

            return part?.name ?? '?';
        }

        return translateKey('finance.fundingServicePart.all');
    }
}
